import { getCountryMoneyFormat, type Country, type MoneyFormat } from '@tectonics/prs/country';

export type FormatDecimal = (value: string) => string;

export type DecimalFormatterArgs = {
  thousandsSeparator: string,
  decimalSeparator: string,
};

export type TruncateDecimal = (value: string) => string;

export const truncateDecimal: TruncateDecimal = (value) => decimalToNumber(value).toFixed(2);

export type GetRatePercentage = (rate: string) => string;

export const getRatePercentage: GetRatePercentage = (rate) => `${parseFloat(rate) * 100}`;

export type DecimalToNumber = (decimal: string) => number;

export const decimalToNumber: DecimalToNumber = (decimal) => {
  const number = Number(decimal);

  if (isNaN(number) || parseFloat(decimal) !== number)
    throw new Error('invalid decimal');

  return number;
};

export function decimalFormatter(
  { thousandsSeparator, decimalSeparator }: DecimalFormatterArgs,
): (value: string) => string {
  const validDecimalPattern = /^(?:\d+(?:\.\d*)?|\.\d+)$/;

  return (value: string): string => {
    if (!validDecimalPattern.test(value)) {
      throw new Error(`Invalid input value: ${value}`);
    }

    const [integerPart = '0', fractionalPart = ''] = value.split('.');

    const formattedInteger = groupThousands(integerPart || '0', thousandsSeparator);
    const formattedFraction = fractionalPart.padEnd(2, '0');

    return `${formattedInteger}${decimalSeparator}${formattedFraction}`;
  };
}

function groupThousands(digits: string, separator: string): string {
  const firstGroupLength = digits.length % 3 || 3;
  const groups = [digits.slice(0, firstGroupLength)];

  for (let i = firstGroupLength; i < digits.length; i += 3) {
    groups.push(digits.slice(i, i + 3));
  }

  return groups.join(separator);
}
export const formatDollar = decimalFormatter({
  thousandsSeparator: ',',
  decimalSeparator: '.',
});

export const formatLatam = decimalFormatter({
  thousandsSeparator: '.',
  decimalSeparator: ',',
});

const moneyFormats: Readonly<Record<MoneyFormat, FormatDecimal>> = {
  dollar: formatDollar,
  latam: formatLatam,
};

export const getMoneyFormat = (country: Country): FormatDecimal => moneyFormats[getCountryMoneyFormat(country)];
